import React, { useState, useEffect, useRef } from 'react';

const SCENARIOS = [
  {
    query: "Show top 10 customers by revenue this month",
    intent: "aggregation", table: "OSQ",
    summary: "Found 10 customers. Rajan Industries leads with ₹24.8L in revenue.",
    rows: [
      { Customer: "Rajan Industries", Revenue: "₹24,80,000", Orders: 12 },
      { Customer: "Mehta Corp",       Revenue: "₹18,20,000", Orders: 8  },
      { Customer: "Patel Traders",    Revenue: "₹15,60,000", Orders: 15 },
    ]
  },
  {
    query: "All open quotations above ₹5,00,000",
    intent: "lookup", table: "OSQ",
    summary: "42 open quotations found. Total pipeline value: ₹3.2 Cr.",
    rows: [
      { "Quote #": "Q-2024-1892", Customer: "Sharma Pvt Ltd", Value: "₹12,40,000" },
      { "Quote #": "Q-2024-1876", Customer: "Gupta Exports",  Value: "₹8,90,000"  },
      { "Quote #": "Q-2024-1845", Customer: "Verma & Sons",   Value: "₹6,20,000"  },
    ]
  },
  {
    query: "Sales trend for last 6 months",
    intent: "trend", table: "OSQ",
    summary: "Revenue grew 23% over 6 months. Best month: March with ₹48.2L.",
    rows: [
      { Month: "Jan 2025", Revenue: "₹35,40,000", Orders: 97  },
      { Month: "Feb 2025", Revenue: "₹41,20,000", Orders: 112 },
      { Month: "Mar 2025", Revenue: "₹48,20,000", Orders: 134 },
    ]
  },
  {
    query: "Pending purchase orders from last week",
    intent: "lookup", table: "OPOR",
    summary: "18 pending POs from last week. 5 are overdue and need attention.",
    rows: [
      { "PO #": "PO-2025-441", Vendor: "Tata Steel",    Amount: "₹9,40,000" },
      { "PO #": "PO-2025-438", Vendor: "Reliance Inc",  Amount: "₹4,20,000" },
      { "PO #": "PO-2025-431", Vendor: "L&T Materials", Amount: "₹7,80,000" },
    ]
  },
  {
    query: "Won vs Lost quotations this quarter",
    intent: "aggregation", table: "OSQ",
    summary: "Win rate 68% this quarter. 124 won (₹2.1Cr) vs 58 lost (₹89L).",
    rows: [
      { Stage: "Won",    Count: 124, "Total Value": "₹2,10,00,000" },
      { Stage: "Lost",   Count: 58,  "Total Value": "₹89,00,000"   },
      { Stage: "Active", Count: 203, "Total Value": "₹4,50,00,000" },
    ]
  }
];

const INTENT_STYLE = {
  aggregation: { bg: 'rgba(27,94,59,0.2)', border: 'rgba(27,94,59,0.45)', color: '#81C784' },
  lookup:      { bg: 'rgba(59,130,246,0.2)', border: 'rgba(59,130,246,0.45)', color: '#93c5fd' },
  trend:       { bg: 'rgba(16,185,129,0.2)', border: 'rgba(16,185,129,0.45)', color: '#6ee7b7' },
};

const KEYFRAMES = `
  @keyframes blink      { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes dotBounce  { 0%,80%,100%{transform:translateY(0);opacity:0.5} 40%{transform:translateY(-7px);opacity:1} }
  @keyframes slideUp    { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes rowIn      { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
  @keyframes pulseGlow  { 0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(16,185,129,0.5)} 50%{opacity:0.7;box-shadow:0 0 0 5px rgba(16,185,129,0)} }
`;

export default function AIDemoPanel() {
  const [idx, setIdx]               = useState(0);
  const [phase, setPhase]           = useState('typing');
  const [typedQuery, setTypedQuery] = useState('');
  const [streamedWords, setStreamedWords] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [showTable, setShowTable]   = useState(false);
  const [fading, setFading]         = useState(false);
  const timerIds = useRef([]);

  const add   = (fn, ms) => { const id = setTimeout(fn, ms); timerIds.current.push(id); };
  const clear = ()       => { timerIds.current.forEach(clearTimeout); timerIds.current = []; };

  useEffect(() => {
    clear();
    const sc = SCENARIOS[idx];

    if (phase === 'typing') {
      setTypedQuery(''); setStreamedWords(0);
      setShowResult(false); setShowTable(false); setFading(false);
      let c = 0;
      const typeChar = () => {
        c++; setTypedQuery(sc.query.slice(0, c));
        if (c < sc.query.length) add(typeChar, 42);
        else add(() => setPhase('thinking'), 350);
      };
      add(typeChar, 550);
    }

    if (phase === 'thinking') add(() => setPhase('streaming'), 1500);

    if (phase === 'streaming') {
      setShowResult(true);
      const words = sc.summary.split(' ');
      let w = 0;
      const streamWord = () => {
        w++; setStreamedWords(w);
        if (w < words.length) add(streamWord, 62);
        else add(() => { setShowTable(true); add(() => setPhase('pausing'), 250); }, 200);
      };
      add(streamWord, 200);
    }

    if (phase === 'pausing') {
      add(() => {
        setFading(true);
        add(() => { setIdx(i => (i + 1) % SCENARIOS.length); setPhase('typing'); }, 650);
      }, 2800);
    }

    return clear;
  }, [phase, idx]);

  const sc   = SCENARIOS[idx];
  const cols = Object.keys(sc.rows[0]);
  const ic   = INTENT_STYLE[sc.intent];
  const words = sc.summary.split(' ');
  const visible = words.slice(0, streamedWords).join(' ');
  const done    = streamedWords >= words.length;

  return (
    <div style={{ ...P.wrap, opacity: fading ? 0 : 1, transition: 'opacity 0.65s ease' }}>
      <style>{KEYFRAMES}</style>

      {/* Header */}
      <div style={P.header}>
        <img
          src="/logo.svg"
          alt="SolutionOne"
          style={{ width: 88, height: 'auto', objectFit: 'contain',
            filter: 'drop-shadow(0 2px 8px rgba(27,94,59,0.40))' }}
        />
        <div style={P.badge}>✦ Live demo</div>
      </div>

      {/* Terminal window */}
      <div style={P.terminal}>
        {/* Title bar */}
        <div style={P.bar}>
          <span style={P.rd}/><span style={P.yl}/><span style={P.gn}/>
          <span style={P.barLabel}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4CAF7D" strokeWidth="2" style={{ marginRight: 4 }}>
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
            ERP Assistant
          </span>
          <span style={P.live}/>
        </div>

        {/* Query row */}
        <div style={P.qrow}>
          <div style={P.qicon}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4CAF7D" strokeWidth="2.5">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </div>
          <div style={P.qtext}>
            {typedQuery
              ? <>{typedQuery}{!showResult && <span style={P.cursor}>|</span>}</>
              : <span style={{ color: 'rgba(255,255,255,0.22)', fontStyle: 'italic' }}>Ask anything about your ERP data…</span>
            }
          </div>
        </div>

        <div style={P.hr}/>

        {/* Thinking dots */}
        {phase === 'thinking' && (
          <div style={P.think}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', marginRight: 8 }}>Analyzing</span>
            {[0,1,2].map(i => <span key={i} style={{ ...P.tdot, animationDelay: `${i * 160}ms` }}/>)}
          </div>
        )}

        {/* Result */}
        {showResult && (
          <div style={{ animation: 'slideUp 0.4s cubic-bezier(0.175,0.885,0.32,1.05)' }}>
            <div style={P.chips}>
              <span style={{ ...P.chip, background: ic.bg, border: `1px solid ${ic.border}`, color: ic.color }}>
                {sc.intent}
              </span>
              <span style={P.tblchip}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginRight: 4 }}>
                  <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                </svg>
                {sc.table}
              </span>
            </div>

            <p style={P.summary}>
              {visible}{!done && <span style={{ ...P.cursor, animation: 'blink 0.65s ease-in-out infinite' }}>|</span>}
            </p>

            {showTable && (
              <div style={P.tbl}>
                <div style={P.thead}>
                  {cols.map(c => <div key={c} style={P.th}>{c}</div>)}
                </div>
                {sc.rows.map((row, ri) => (
                  <div key={ri} style={{ ...P.trow, animation: `rowIn 0.35s ease ${ri * 90}ms both` }}>
                    {cols.map(c => <div key={c} style={P.td}>{row[c]}</div>)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={P.footer}>
        <div style={{ display: 'flex', gap: 3, marginBottom: 5 }}>
          {[0,1,2,3,4].map(i => (
            <svg key={i} width="11" height="11" viewBox="0 0 24 24" fill="#fbbf24">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          Trusted by <span style={{ color: '#4CAF7D', fontWeight: 600 }}>50+ teams</span> across India
        </div>
        <div style={P.sdots}>
          {SCENARIOS.map((_, i) => (
            <div key={i} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: i === idx ? '#1B5E3B' : 'rgba(255,255,255,0.2)',
              transform: i === idx ? 'scale(1.4)' : 'scale(1)',
              transition: 'all 0.4s ease',
            }}/>
          ))}
        </div>
      </div>
    </div>
  );
}

const P = {
  wrap: {
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '48px 44px', height: '100%', boxSizing: 'border-box',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  badge: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#4CAF7D',
    background: 'rgba(27,94,59,0.15)', border: '1px solid rgba(27,94,59,0.3)',
    borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap',
  },
  terminal: {
    background: 'rgba(6,4,16,0.75)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.09)', borderRadius: 14, overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(27,94,59,0.08)',
    minHeight: 260,
  },
  bar: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.035)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  rd: { width: 10, height: 10, borderRadius: '50%', background: '#ff5f57', display: 'inline-block', flexShrink: 0 },
  yl: { width: 10, height: 10, borderRadius: '50%', background: '#febc2e', display: 'inline-block', flexShrink: 0 },
  gn: { width: 10, height: 10, borderRadius: '50%', background: '#28c840', display: 'inline-block', flexShrink: 0 },
  barLabel: {
    display: 'flex', alignItems: 'center',
    fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)',
    marginLeft: 6, flex: 1,
  },
  live: {
    width: 7, height: 7, borderRadius: '50%',
    background: '#10b981', flexShrink: 0,
    animation: 'pulseGlow 2s ease-in-out infinite',
  },
  qrow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 14px' },
  qicon: {
    width: 26, height: 26, borderRadius: 7, flexShrink: 0, marginTop: 1,
    background: 'rgba(27,94,59,0.18)', border: '1px solid rgba(27,94,59,0.32)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  qtext: { fontSize: 13, fontWeight: 500, color: '#e2e8f0', lineHeight: 1.5, flex: 1 },
  cursor: {
    display: 'inline', color: '#4CAF7D', fontWeight: 300, marginLeft: 1,
    animation: 'blink 1.05s ease-in-out infinite',
  },
  hr: { height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 14px' },
  think: { display: 'flex', alignItems: 'center', padding: '13px 14px' },
  tdot: {
    width: 6, height: 6, borderRadius: '50%', background: '#4CAF7D',
    display: 'inline-block', marginRight: 4,
    animation: 'dotBounce 1s ease-in-out infinite',
  },
  chips: { display: 'flex', gap: 6, padding: '11px 14px 5px' },
  chip: {
    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
    letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  tblchip: {
    display: 'flex', alignItems: 'center',
    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)',
    color: 'rgba(255,255,255,0.55)', letterSpacing: '0.4px',
  },
  summary: {
    fontSize: 12.5, color: 'rgba(255,255,255,0.72)',
    padding: '4px 14px 10px', lineHeight: 1.6, margin: 0,
  },
  tbl: {
    margin: '0 14px 14px', borderRadius: 8, overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  thead: {
    display: 'flex', background: 'rgba(27,94,59,0.14)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  th: {
    flex: 1, padding: '6px 10px', fontSize: 10, fontWeight: 700,
    color: '#4CAF7D', textTransform: 'uppercase', letterSpacing: '0.4px',
  },
  trow: { display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  td:   { flex: 1, padding: '7px 10px', fontSize: 11.5, color: 'rgba(255,255,255,0.72)' },
  footer: { marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  sdots: { display: 'flex', gap: 6, justifyContent: 'center', marginTop: 14 },
};
